import classnames from 'classnames'
import { format } from 'date-fns'
import * as React from 'react'

import { TableAlign } from '../../components/Table'
import { getHeatmapClassNames } from '../../components/financials-table'
import { FinancialsColumnDefinition } from '../../components/financials-table/types'
import { shortFormatNumber } from '../../util'
import { COMMON_COLUMN_CLASS_NAME } from './constants'
import { TrafficDataViewEnum, TrafficSubRowDataType } from './types'

const getColumnsAndValueGetter = (yearKeys: string[], trafficDataView: TrafficDataViewEnum) => {
  const isMomChangePct = trafficDataView === TrafficDataViewEnum.momChangePct
  const columns: FinancialsColumnDefinition<TrafficSubRowDataType>[] = yearKeys.map((yearMonth) => {
    const year = yearMonth.slice(0, 4)
    const month = yearMonth.slice(4, 6)
    const monthIndex = Number.parseInt(month) - 1
    const date = new Date(Number.parseInt(year), monthIndex, 1)
    return {
      id: yearMonth,
      name: format(date, monthIndex === 0 ? 'yyyy' : 'MMM'),
      align: TableAlign.Right,
      className: classnames(COMMON_COLUMN_CLASS_NAME, {
        'is-heatmap': isMomChangePct,
      }),
      getColumnClassName: (data) => (isMomChangePct ? getHeatmapClassNames(data.momKeyValueMap[yearMonth]) : null),
      renderColumn: (data) => {
        const value = data[isMomChangePct ? 'momKeyValueMap' : 'keyValueMap'][yearMonth] ?? null
        const hasValue = typeof value === 'number'
        const shouldShowDecimal = (value ?? 0) >= 1000 || isMomChangePct
        return (
          <span className={classnames({ 'text-muted-2': !hasValue })}>
            {shortFormatNumber(value, { fractions: shouldShowDecimal ? 1 : 0, defaultValue: '—' })}
            {isMomChangePct && hasValue ? '%' : ''}
          </span>
        )
      },
    }
  })

  return {
    columns,
    valueGetter: (column: FinancialsColumnDefinition<TrafficSubRowDataType>) => (data: TrafficSubRowDataType) =>
      data[isMomChangePct ? 'momKeyValueMap' : 'keyValueMap'][column.id] ?? null,
  }
}

const EMPTY_ARRAY: FinancialsColumnDefinition<TrafficSubRowDataType>[] = []

export function useTableColumns(yearKeys: string[], trafficDataView: TrafficDataViewEnum, hasBeenInView: boolean) {
  return React.useMemo(
    () =>
      hasBeenInView
        ? getColumnsAndValueGetter(yearKeys, trafficDataView)
        : { columns: EMPTY_ARRAY, valueGetter: () => () => null },
    [yearKeys, trafficDataView, hasBeenInView]
  )
}
